<template>
  <div>
    <form class="store-locator__control">
      <div class="form-group">
        <label
          for="zipcode"
          class="input-text__label is-sr-only"
        >
          {{ zipcodeLabel }}
        </label>
        <input
          id="zipcode"
          v-model="zipcode"
          class="input-text input-text--show-placeholder"
          type="text"
          name="zipcode"
          aria-describedby="zipcode-errors"
          :placeholder="zipcodeLabel"
          @change="dirtyZipCode"
          @keypress="isNumber($event)"
        >
        <div
          id="zipcode-errors"
          class="errors-log"
        >
          <span v-if="zipcodeError"><slot name="zipcode-error" /></span>
        </div>
      </div>

      <div class="form-group form-group--select">
        <label
          for="products"
          class="is-sr-only"
        >
          <slot name="product-label" />
        </label>
        <select
          id="products"
          v-model="product"
          name="products"
          class="input-text"
          aria-describedby="products-errors"
          aria-required="true"
          @change="dirtyProduct"
        >
          <option
            disabled
            selected
            value=""
          >
            <slot name="product-label" />
          </option>
          <option
            v-for="({ id, value }) in products"
            :key="id"
            :value="id"
            v-text="value"
          />
        </select>
        <div
          id="products-errors"
          class="errors-log"
        >
          <span v-if="productError"><slot name="product-error" /></span>
        </div>
      </div>

      <div class="form-group form-group--select">
        <label
          for="distance"
          class="is-sr-only"
        >
          <slot name="store-distance-label" />
        </label>
        <select
          id="distance"
          v-model="distance"
          name="distance"
          class="input-text"
          aria-describedby="distance-errors"
          aria-required="true"
          @change="dirtyDistance"
        >
          <option
            disabled
            selected
            value=""
          >
            <slot name="store-distance-label" />
          </option>
          <option
            v-for="({ id, value }) in distances"
            :key="id"
            :value="id"
            v-text="value"
          />
        </select>
        <div
          id="distance-errors"
          class="errors-log"
        >
          <span v-if="distanceError"><slot name="store-distance-error" /></span>
        </div>
      </div>

      <button
        class="btn btn--secondary"
        @click.prevent="submit"
      >
        <slot name="cta-label" />
      </button>
    </form>

    <div class="store-locator__copy">
      <p class="paragraph">
        <slot name="product-availability-label" />
      </p>
    </div>

    <store-results
      :selected-product="selectedProduct"
      :results="results"
      :no-results="noResults"
      :google-maps-url="googleMapsUrl"
      :default-zoom="defaultZoom"
      :default-lat="defaultLat"
      :default-long="defaultLong"
      :loading="loading"
    >
      <template slot="placeholder-google-maps">
        <slot name="placeholder-google-maps" />
      </template>
      <template slot="loading-icon">
        <slot name="loading-icon" />
      </template>
      <template slot="enter-info-copy">
        <slot name="enter-info-copy" />
      </template>
      <template slot="results-title-copy">
        <slot name="results-title-copy" />
      </template>
      <template slot="results-arrow">
        <slot name="results-arrow" />
      </template>
      <template slot="no-locations-fould">
        <slot name="no-locations-fould" />
      </template>
    </store-results>
  </div>
</template>

<script>
  import StoreResults from '../StoreResults/StoreResults';
  import { AnalyticsHandler } from '@Foundation/analyticsHandler';

  export default {
    name: 'StoreLocator',
    components: {
      StoreResults
    },
    props: {
      apiUrl: { type: String, required: true },
      pageCategory: {
        default: '',
        type: String,
      },
      products: { type: Array, required: true },
      distances: { type: Array, required: true },
      googleMapsUrl: { type: String, required: true },
      defaultZoom: { type: Number, required: true },
      defaultLat: { type: String, required: true },
      defaultLong: { type: String, required: true },
      zipcodeLabel: { type: String, required: true },
      zipcodeSizeValidation: { type: Number, required: false, default: 5 }
    },
    data() {
      return {
        results: [],
        noResults: false,
        loading: false,
        zipcode: '',
        zipcodeDirty: false,
        product: '',
        productDirty: false,
        distance: '',
        distanceDirty: false
      };
    },
    computed: {
      zipcodeError() {
        return this.zipcodeDirty && this.zipcode.length !== this.zipcodeSizeValidation;
      },
      productError() {
        return this.product === '' && this.productDirty;
      },
      distanceError() {
        return this.distance === '' && this.distanceDirty;
      },
      allFilled() {
        return !this.zipcodeError && !this.productError && !this.distanceError;
      },
      selectedProduct() {
        return (this.product && this.products.find((product) => product.id === this.product)) || {};
      },
      taggingEvent() {
        const product = this.selectedProduct;
        const distanceLabel = this.distances.find(el => el.id == this.distance).value;
        product.category = product.category ? product.category.toLowerCase() : '';
        product.name = product.value.toLowerCase();

        return {
          event: 'uaevent',
          ecommerce: 'undefined',
          eventCategory: 'store locator',
          eventAction: 'find a salon page',
          eventLabel: `${this.pageCategory.toLowerCase()}::${product.ean}::${product.name}`,
          event_name: 'store_locator',
          product_info: `${product.name}::${product.id}`,
          search_category: product.name,
          search_keyword: `${this.zipcode}::${distanceLabel}`,
          position: 'on page',
        };
      }
    },
    mounted() {
      this.setProductBasedOnParams();
      this.setZipcodeBasedOnParams();
      this.setDefaultDistance();

      if (this.product && this.zipcode) {
        this.submit();
      }
    },
    methods: {
      setDefaultDistance() {
        this.distance = this.distances[0] && this.distances[0].id;
      },
      getProductIdFromParams(param = 'id') {
        const productId = window.location.search && new URLSearchParams(window.location.search).get(param);
        return productId ? productId : '';
      },
      setProductBasedOnParams() {
        this.product = this.getProductIdFromParams();
      },
      getZipcodeFromParams(param = 'zip') {
        const zipcode = window.location.search && new URLSearchParams(window.location.search).get(param);
        return zipcode ? zipcode : '';
      },
      setZipcodeBasedOnParams() {
        this.zipcode = this.getZipcodeFromParams();
      },
      submit() {
        this.setAllDirty();

        if (this.allFilled) {
          this.loading = true;
          this.results = [];
          this.noResults = false;

          const parseJSON = (response) => response.json();
          this.sendTaggingEvent();

          fetch(`${this.apiUrl}?zipcode=${this.zipcode}&product=${this.product}&distance=${this.distance}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json;charset=utf-8' },
            credentials: 'same-origin',
          })
            .then(parseJSON)
            .then(data => {
              if (data.stores && data.stores.length > 0) {
                this.results = data.stores;
              } else {
                this.noResults = true;
              }
              this.loading = false;
            })
            .catch(error => {
              this.loading = false;
              console.error('Could not load stores', error); // eslint-disable-line no-console
            });
        }
      },
      setAllDirty() {
        this.zipcodeDirty = true;
        this.productDirty = true;
        this.distanceDirty = true;
      },
      dirtyZipCode() {
        this.updateUrlZip();
        this.zipcodeDirty = true;
      },
      dirtyProduct() {
        this.updateUrlProduct();
        this.productDirty = true;
      },
      dirtyDistance() {
        this.distanceDirty = true;
      },
      sendTaggingEvent() {
        AnalyticsHandler.pushDataLayer(this.taggingEvent);
      },
      updateUrlZip() {
        if (window.location.search && new URLSearchParams(window.location.search).get('zip')) {
          const url = new URL(window.location.origin + window.location.pathname);
          const params = new URLSearchParams(window.location.search);
          params.set('zip', this.zipcode.toString());
          url.search = params.toString();
          window.history.pushState({ path: url.href }, '', url.href);
        }
      },
      updateUrlProduct() {
        if (window.location.search && new URLSearchParams(window.location.search).get('id')) {
          const url = new URL(window.location.origin + window.location.pathname);
          const params = new URLSearchParams(window.location.search);
          params.set('id', this.product.toString());
          url.search = params.toString();
          window.history.pushState({ path: url.href }, '', url.href);
        }
      },
      isNumber: function(evt) {
        evt = (evt) ? evt : window.event;
        var charCode = (evt.which) ? evt.which : evt.keyCode;
        if ((charCode > 31 && (charCode < 48 || charCode > 57))) {
          evt.preventDefault();
        } else {
          return true;
        }
      }
    }
  };
</script>
